import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core'
import * as mapLibre from 'maplibre-gl'
import { LngLat, MapOptions, Offset, Point } from 'maplibre-gl'
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
} from 'rxjs'
import { environment } from '../../../../environments/environment'
import { GERMANY_CENTER, LATITUDE_LIMITS, MAP_COLOR_SCHEME, MAP_STYLE, NAME_LAYOUT_LANGUAGE } from './map-config'
import { AddressSelectItem } from './map-model'
import { FormGroup } from '@angular/forms'
import { GenericResourceFormControls } from '../../../shared/models/technical-resource'
import { ProductionTechnology } from '../../../shared/models/production-technology.enum'
import { MapService } from './map.service'
import { BricksIconComponent } from '@enbw_bricks/ui'
import { POSTAL_CODE_PATTERN } from '../../../shared/validators/validator-pattern'
import { MatSnackBar } from '@angular/material/snack-bar'
import { SnackbarContentComponent } from '../../../shared/components/snackbar/snackbar.content.component'
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete'
import { MatFormField, MatInput, MatLabel } from '@angular/material/input'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AbstractTrackEventsService } from '../../../shared/services/abstract-track-events.service'

export function mapLibreInitOptions(containerId: string): MapOptions {
  return {
    attributionControl: false,
    container: containerId,
    style: `https://maps.geo.eu-central-1.amazonaws.com/v2/styles/${MAP_STYLE}/descriptor?key=${environment.awsLocationApiKey}&color-scheme=${MAP_COLOR_SCHEME}`,
    center: [GERMANY_CENTER[0], GERMANY_CENTER[1]],
    zoom: 5,
    scrollZoom: false,
    locale: {
      'NavigationControl.ZoomIn': 'Vergrößern',
      'NavigationControl.ZoomOut': 'Verkleinern',
    },
  }
}

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrl: 'map.component.scss',
  imports: [BricksIconComponent, MatAutocomplete, MatOption, MatInput, MatFormField, MatLabel, MatAutocompleteTrigger],
})
export class MapComponent implements AfterViewInit, OnInit, OnDestroy {
  readonly resource = input.required<FormGroup<GenericResourceFormControls>>()
  readonly resourceId = input.required<number>()
  readonly technology = input.required<Signal<ProductionTechnology | null>>()
  readonly isLocationValid = input.required<WritableSignal<boolean>>()

  protected readonly isPinValid = signal<boolean>(true)
  protected readonly addressHits = signal<AddressSelectItem[]>([])

  private readonly trackEventService = inject(AbstractTrackEventsService)
  private readonly mapService = inject(MapService)
  private readonly snackBar = inject(MatSnackBar)
  private readonly addressSearch$ = new Subject<string>()

  private map: mapLibre.Map | undefined
  private pin: mapLibre.Marker | undefined
  private lockedPin: mapLibre.Marker | undefined

  private readonly pinLabelOffset: Offset = {
    top: new Point(0, 0),
    center: new Point(0, 0),
    bottom: new Point(0, -65),
    left: new Point(25, -30),
    right: new Point(-25, -30),
    'top-left': new Point(0, 0),
    'top-right': new Point(0, 0),
    'bottom-left': new Point(0, -65),
    'bottom-right': new Point(0, -65),
  }

  private readonly destroyRef = inject(DestroyRef)

  constructor() {
    this.addressSearch$.pipe(debounceTime(500), distinctUntilChanged()).subscribe((value) => this.searchAddress(value))
  }

  ngOnInit() {
    combineLatest([
      this.resource().controls.longitude.valueChanges.pipe(startWith(this.resource().controls.longitude.value)),
      this.resource().controls.latitude.valueChanges.pipe(startWith(this.resource().controls.latitude.value)),
    ])
      .pipe(
        distinctUntilChanged(([prevLon, prevLat], [currLon, currLat]) => prevLon === currLon && prevLat === currLat),
        filter(
          ([lon, lat]) =>
            lon !== null &&
            lat !== null &&
            String(lon) !== '' &&
            String(lat) !== '' &&
            Number(lat) >= LATITUDE_LIMITS.min &&
            Number(lat) <= LATITUDE_LIMITS.max
        ),
        switchMap(([lon, lat]) =>
          this.setLockedPin(new LngLat(lon as number, lat as number)).pipe(
            catchError((err) => {
              console.error('Error setting locked pin:', err)
              this.isLocationValid().set(false)
              return of()
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe()
  }

  ngAfterViewInit(): void {
    this.map = new mapLibre.Map(mapLibreInitOptions(`map${this.resourceId()}`))

    this.map.addControl(
      new mapLibre.NavigationControl({
        showZoom: true,
        showCompass: false,
      }),
      'bottom-right'
    )
    this.map.on('load', () => {
      this.map!.resize()
      this.updateMapLanguage()
    })
    this.map.on('click', (e) => this.onMapClicked(e.lngLat))

    const canvas = this.map.getCanvas()
    canvas.style.cursor = 'default'
    this.map.on('dragstart', () => (canvas.style.cursor = 'grabbing'))
    this.map.on('dragend', () => (canvas.style.cursor = 'default'))
  }

  // locked pin handlers
  lockPin() {
    if (this.pin && this.isPinValid()) {
      this.resource().patchValue({
        longitude: this.formatCoordinate(this.pin.getLngLat().lng),
        latitude: this.formatCoordinate(this.pin.getLngLat().lat),
      })
    }
  }

  private updateMapLanguage() {
    const style = this.map?.getStyle()
    if (!style) return

    style.layers.forEach((layer) => {
      if (layer.layout && (layer.layout as never)['text-field']) {
        this.map?.setLayoutProperty(layer.id, 'text-field', [
          'coalesce',
          ['get', `name:${NAME_LAYOUT_LANGUAGE}`],
          ['get', 'name'],
        ])
      }
    })
  }

  private setLockedPin(lngLat: LngLat): Observable<void> {
    return this.mapService.reverseGeocoding(lngLat).pipe(
      map((items: AddressSelectItem[]) => {
        if (items.length > 0) {
          this.isLocationValid().set(true)
          if (this.lockedPin) {
            this.lockedPin.setLngLat(lngLat)
          } else {
            this.createLockedPin(lngLat)
          }
          this.lockedPin?.getPopup().setHTML(this.buildPinLabel(items[0].label))
          this.centerMap(this.lockedPin!)
          this.pin?.remove()
          delete this.pin
        } else {
          // if coordinates input from user is outside Germany
          this.isLocationValid().set(false)
          this.showErrorMessage()
          this.isPinValid.set(false)
          this.lockedPin?.remove()
          delete this.lockedPin
          this.setPin(lngLat)
        }
      })
    )
  }

  private createLockedPin(lngLat: LngLat) {
    this.lockedPin = new mapLibre.Marker({ element: this.buildLockedPinIcon(), anchor: 'bottom' })
      .setLngLat(lngLat)
      .setDraggable(false)
      .setPopup(
        new mapLibre.Popup({
          offset: this.pinLabelOffset,
          closeOnClick: false,
          closeButton: false,
        })
      )
      .addTo(this.map!)

    this.lockedPin.getElement().addEventListener('mouseenter', () => this.lockedPin!.togglePopup())
    this.lockedPin.getElement().addEventListener('mouseleave', () => this.lockedPin!.togglePopup())
    // prevent show pop up on pin click
    this.lockedPin.getElement().addEventListener('click', (e: MouseEvent) => e.stopPropagation())
  }

  private buildLockedPinIcon(): HTMLElement {
    const icon = `assets/images/pin-${this.technology()() == ProductionTechnology.Solar ? 'solar' : 'wind'}.svg`
    const el = document.createElement('div')
    el.innerHTML = `
   <div class="image-container">
  <img src="${icon}" alt="Resource" />
    </div>
  `
    return el
  }

  private buildPinLabel(text: string): string {
    return `<div class="pin-label">Stromerzeugungseinheit (TR-${this.resourceId() + 1})<br/>${text}</div>`
  }

  private formatCoordinate(value: number): number {
    const parts: string[] = value.toFixed(14).split('.')
    let decPart = parts[1].replace(/0+$/, '')
    if (decPart.length < 3) {
      decPart = decPart.padEnd(3, '0')
    }
    return Number(`${parts[0]}.${decPart}`)
  }

  // search input handlers
  onAddressInput(event: Event) {
    const value = (event.target as HTMLInputElement).value?.trim()
    this.addressSearch$.next(value)
  }

  displayLabel(option: AddressSelectItem): string {
    return option?.label ?? ''
  }

  onAddressSelected(placeId: string) {
    this.mapService.getPlaceById(placeId).subscribe((place) => {
      if (place) {
        this.isPinValid.set(true)
        this.setPin(new LngLat(place.Position[0], place.Position[1]))
        if (place.MapView) {
          this.map?.fitBounds(
            [
              [place.MapView[0], place.MapView[1]],
              [place.MapView[2], place.MapView[3]],
            ],
            { padding: 50 }
          )
        } else {
          this.centerMapIfMarkerOutside(this.pin!)
        }
      }
    })
  }

  private searchAddress(query: string) {
    query = query?.trim()
    let addressHits$: Observable<AddressSelectItem[]>
    if (!query || query.length < 1) {
      addressHits$ = of([])
    } else if (new RegExp(POSTAL_CODE_PATTERN).exec(query)) {
      addressHits$ = this.mapService.searchAddressWithPostalCode(query)
    } else {
      addressHits$ = this.mapService.searchAddressWithQuery(query)
    }
    addressHits$.subscribe((addressHits) => this.addressHits.set(addressHits))
  }

  private onMapClicked(lngLat: LngLat) {
    this.mapService.reverseGeocoding(lngLat).subscribe((items) => {
      if (items.length > 0) {
        this.setPin(lngLat)
        this.isPinValid.set(true)
      } else {
        this.isPinValid.set(false)
        this.showErrorMessage()
      }
    })
  }

  // movable pin handlers

  private setPin(lngLat: LngLat) {
    if (this.pin) {
      this.pin.setLngLat(lngLat)
    } else {
      this.pin = new mapLibre.Marker().setLngLat(lngLat).setDraggable(true).addTo(this.map!)
      this.pin.on('dragend', () => this.onMapClicked(this.pin!.getLngLat()))
    }
  }

  private showErrorMessage() {
    this.snackBar.openFromComponent(SnackbarContentComponent, {
      data: { message: 'Nur Koordinaten innerhalb Deutschlands zulässig.' },
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['snackbar-error'],
    })
  }

  private centerMapIfMarkerOutside(pin: mapLibre.Marker): void {
    const lngLat = pin.getLngLat()
    const bounds = this.map?.getBounds()
    if (!bounds?.contains(lngLat)) {
      this.centerMap(pin)
    }
  }

  private centerMap(pin: mapLibre.Marker): void {
    this.map?.jumpTo({ center: pin.getLngLat(), zoom: 16 })
  }

  trackEvent() {
    this.trackEventService.trackEvent('location_set', 'Button', 'click', 'Set Location')
  }

  ngOnDestroy() {
    this.addressSearch$?.unsubscribe()
  }
}
